/**
 * One alert-rules save queue PER PET, at module level, so it outlives every
 * AlertRulesSection instance (vic/vetpo#36; TLA+ docs/spec/tla/AlertRulesCoordinator.tla).
 *
 * The editor is keyed by pet, so leaving a pet UNMOUNTS its editor. Its save --
 * the one the debounce started, or the flush of edits it had not saved yet --
 * keeps running here, and the next editor of that pet waits for `whenRulesSavesIdle`
 * before it loads, so it can never read the rules before that save lands.
 *
 * Per pet: at most one job RUNNING, plus one PENDING (coalesced -- a newer
 * enqueue replaces it). A job snapshots the editor's rules when it STARTS, so
 * a pending job always saves the latest state. A job that throws or decides to
 * do nothing (invalid snapshot, nothing new) simply ends, and the queue moves on.
 *
 * Server DELETEs of a pet's rules (the remove button, a save's orphan cleanup)
 * are not jobs -- they must not wait behind a save -- but `trackRulesWrite`
 * counts them in, so "idle" also means "no delete in flight": the next editor
 * cannot load a rule whose delete has not landed yet.
 */
type Job = () => Promise<void>

interface PetQueue {
  running: boolean
  pending: Job | null
  inFlight: number // tracked writes outside the job line (deletes)
  idleWaiters: Array<() => void>
}

const queues = new Map<string, PetQueue>()

const keyOf = (pet: number | string) => String(pet)

const queueFor = (pet: number | string): PetQueue => {
  const key = keyOf(pet)
  let q = queues.get(key)
  if (!q) {
    q = { running: false, pending: null, inFlight: 0, idleWaiters: [] }
    queues.set(key, q)
  }
  return q
}

const isIdle = (q: PetQueue) => !q.running && !q.pending && q.inFlight === 0

// Wake the waiters and drop an idle entry, so a long session does not keep
// one entry per pet it ever visited.
const settleIfIdle = (pet: number | string, q: PetQueue) => {
  if (!isIdle(q)) return
  for (const wake of q.idleWaiters.splice(0)) wake()
  if (queues.get(keyOf(pet)) === q) queues.delete(keyOf(pet))
}

const pump = (pet: number | string, q: PetQueue): void => {
  if (q.running) return
  const job = q.pending
  if (!job) {
    settleIfIdle(pet, q)
    return
  }
  q.pending = null
  q.running = true
  Promise.resolve()
    .then(job)
    .catch((error) => console.error('Alert rules save failed:', error))
    .finally(() => {
      q.running = false
      pump(pet, q)
    })
}

/** Queue a save for `pet` (coalesced with any not-yet-started one). */
export const enqueueRulesSave = (pet: number | string, job: Job): void => {
  const q = queueFor(pet)
  q.pending = job
  pump(pet, q)
}

/** Count a write for `pet` that runs outside the job line (a delete) into "busy". */
export const trackRulesWrite = <T>(pet: number | string, write: Promise<T>): Promise<T> => {
  const q = queueFor(pet)
  q.inFlight += 1
  const done = () => {
    q.inFlight -= 1
    settleIfIdle(pet, q)
  }
  write.then(done, done)
  return write
}

/** Resolves once `pet` has no save running or pending and no tracked write in flight. */
export const whenRulesSavesIdle = (pet: number | string): Promise<void> => {
  const q = queues.get(keyOf(pet))
  if (!q || isIdle(q)) return Promise.resolve()
  return new Promise((resolve) => q.idleWaiters.push(resolve))
}

/** A save for `pet` is running right now. (Never creates an entry.) */
export const isRulesSaveRunning = (pet: number | string): boolean =>
  queues.get(keyOf(pet))?.running ?? false

/**
 * Test teardown. A job that already STARTED cannot be cancelled -- it is awaiting
 * its own requests -- so first let every queue run to idle (bounded, microtasks
 * only, so it also works under fake timers), then forget whatever is left.
 */
export const __resetRulesSaveQueuesForTests = async (): Promise<void> => {
  for (let i = 0; i < 200 && Array.from(queues.values()).some((q) => !isIdle(q)); i++) {
    await Promise.resolve()
  }
  // A job still running here awaits a request that never answers (a test's
  // HELD request): it outlives the test by design, and the file that holds
  // requests must make its mocks ignore it (alertRulesAutosave.race.test.tsx
  // stamps each test's mocks with an epoch). Dropping its entry keeps the next
  // test's editor from waiting on it.
  queues.clear()
}
